import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import cx from 'classnames';
import BottomModal from '../../../components/BottomModal';
import UpButton from '../../../components/UpButton';
import UpInput from '../../../components/UpInput';
import { toast } from '../../../components/UpToast';
import { updateOrder } from '../../../services/api';
import { formatMoney } from '../../../helpers/utils';
import { ApiConstants } from '../../../constants/ApiConstants';
import type {
  Customer,
  OrderOrder,
  TippingPreset,
} from '../../../types/codexOrder';

const CUSTOM = 'custom';
type Selection = number | typeof CUSTOM;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function TipModal({
  order,
  orderId,
  customer,
  presets,
  isOpen,
  onClose,
}: {
  orderId: string;
  isOpen: boolean;
  order: OrderOrder;
  customer: Customer | undefined;
  presets: TippingPreset[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const subtotal = order?.details?.order_subtotal ?? 0;
  const appliedPct = order?.details?.tipping_percentage ?? 0;

  const preferredPct = useMemo(
    () => presets.find(p => p.default)?.value ?? presets[0]?.value ?? 0,
    [presets],
  );

  const [selection, setSelection] = useState<Selection>(preferredPct);
  const [customPct, setCustomPct] = useState('');

  // Selection resets to the applied tip (or the preferred preset) on open.
  useEffect(() => {
    if (!isOpen) return;
    const matchesPreset = presets.some(p => p.value === appliedPct);
    if (appliedPct > 0 && matchesPreset) {
      setSelection(appliedPct);
      setCustomPct('');
    } else if (appliedPct > 0) {
      setSelection(CUSTOM);
      setCustomPct(String(appliedPct));
    } else {
      setSelection(preferredPct);
      setCustomPct('');
    }
  }, [isOpen, appliedPct, preferredPct, presets]);

  const effectivePct =
    selection === CUSTOM ? Number(customPct) || 0 : selection;
  const previewAmount = round2((subtotal * effectivePct) / 100);

  const { mutate: callUpdateOrder, isPending } = useMutation({
    mutationFn: (params: unknown) => updateOrder(params),
    onSuccess: resp => {
      queryClient.setQueryData(['getOrderDetails', orderId], resp);
      onClose();
    },
    onError: err => {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.info(
        error?.response?.data?.detail ??
          'Something went wrong. Could not update the tip.',
      );
    },
  });

  function buildBaseParams() {
    const params: {
      reference_id: string;
      order_type?: string;
      payment_option?: string;
      address_id?: string;
    } = {
      reference_id: orderId,
      order_type: order?.details?.order_type,
      payment_option: order?.details?.pay_by,
    };
    if (
      customer?.address?.id &&
      order?.details?.order_type === ApiConstants.TYPE_DELIVERY
    ) {
      params.address_id = String(customer.address.id);
    }
    return params;
  }

  function onAddTip() {
    if (isPending || effectivePct <= 0) return;
    callUpdateOrder({ ...buildBaseParams(), tipping_percentage: effectivePct });
  }

  function onRemoveTip() {
    if (isPending) return;
    // An explicit 0 clears the tip; omitting it would preserve the existing one.
    callUpdateOrder({ ...buildBaseParams(), tipping_percentage: 0 });
  }

  const chipClass = (active: boolean) =>
    cx(
      'flex-1 py-2.5 rounded-lg border text-sm font-[500] cursor-pointer text-center',
      active
        ? 'border-brand-icon text-brand-icon bg-brand-icon/5'
        : 'border-dashed border-up-black-20 text-up-black-60',
    );

  return (
    <BottomModal isOpen={isOpen} onClose={onClose} height="auto" hideClose>
      <div className="px-2 pb-2">
        <p className="text-center text-up-black-50 text-sm">Tip Amount</p>
        <p className="text-center text-2xl font-bold mt-2 mb-6">
          {formatMoney(previewAmount)}
          {selection !== CUSTOM && effectivePct > 0 && (
            <span className="text-up-black-50 text-base font-normal">
              {' '}
              ({effectivePct}%)
            </span>
          )}
        </p>

        <div className="flex items-center gap-x-3 mb-4">
          {presets.map(preset => (
            <div
              key={preset.label}
              className={chipClass(selection === preset.value)}
              onClick={() => {
                setSelection(preset.value);
              }}>
              {preset.value}%
            </div>
          ))}
          <div
            className={chipClass(selection === CUSTOM)}
            onClick={() => {
              setSelection(CUSTOM);
            }}>
            Custom
          </div>
        </div>

        {selection === CUSTOM && (
          <UpInput
            className="mb-4"
            type="number"
            inputMode="decimal"
            placeholder="Enter tip percentage"
            value={customPct}
            onChange={e => {
              setCustomPct(e.target.value);
            }}
          />
        )}

        <UpButton
          className="mb-2"
          loading={isPending}
          disabled={effectivePct <= 0}
          onClick={onAddTip}>
          Add Tip
        </UpButton>

        {appliedPct > 0 ? (
          <button
            type="button"
            onClick={onRemoveTip}
            className="w-full text-center py-3 text-sm font-[500] text-up-red-50 cursor-pointer">
            Remove tip
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="w-full text-center py-3 text-sm font-[500] text-up-black-60 cursor-pointer">
            Cancel
          </button>
        )}
      </div>
    </BottomModal>
  );
}

export default TipModal;
